<script setup lang="ts">
//#region imports
import { ref } from 'vue';
import { useError } from '@/composables/useError';
import { useApi } from '@/api';
import {
  type EmitsToListeners, type ErrorResponse,
  type ToErrorMap, typeGuard,
} from '@/types/core';
import type {
  PostMediaBuyingUrlBlacklistsData,
  PutMediaBuyingUrlBlacklistsByIdData,
} from '@/types/openapi-ts';
//#endregion

//#region types
type FormErrors = ToErrorMap<
  PutMediaBuyingUrlBlacklistsByIdData['body'] & PostMediaBuyingUrlBlacklistsData['body']
>;

interface Props {
  id?: string | null;
  url?: string;
}

interface  Emits {
  'close': [],
  'success': [],
}

export interface BlacklistUrlParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  id: null,
  url: '',
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { getError, errors } = useError<NonNullable<FormErrors>>();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const urls = ref(props.url ? [props.url] : ['']);
//#endregion

//#region methods
function open() {
  isVisible.value = true;
  isLoading.value = false;
  errors.value = {};
}

const handleClose = () => {
  emit('close');
  isVisible.value = false;
};

function handleSave() {
  isLoading.value = true;
  const urlsFiltered = urls.value.filter((e) => e !== '');
  urls.value = urlsFiltered.length ? urlsFiltered : [''];

  const promise = props.id
    ? api.putMediaBuyingBlacklistUrls(props.id, { url: urls.value[0]! })
    : api.postMediaBuyingBlacklistUrls({ urls: urls.value });

  promise.then(() => {
      emit('success');
      handleClose();
  }).catch((e) => {
    if(typeGuard<ErrorResponse<FormErrors>>(e)){
      errors.value = e?.data?.errors || {};
    }
  }).finally(() => isLoading.value = false);
}

function clearError(i: number) {
  if (getError(['urls', i])) {
    errors.value.urls![i] = '';
  }
}

function popUrl(i: number) {
  urls.value.splice(i, 1);
}

function addUrl() {
  errors.value = {};
  urls.value.push('');
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-blacklist-popup"
    :title="id
      ? $t('mediaBuying.blacklistUrls.popup.titleEdit')
      : $t('mediaBuying.blacklistUrls.popup.title')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-8">
        <UiLabel
          label="URL"
        />
        <div
          v-for="(_, i) in urls"
          :key="i"
          class="t:flex t:items-start t:space-x-8"
        >
          <UiInput
            v-model="urls[i]"
            placeholder="URL"
            size="big"
            autocomplete="off"
            autosize
            type="text"
            :error="getError('url') || getError('urls') || getError(['urls', i])"
            @keydown.enter.prevent
            @update:model-value="clearError(i)"
          />
          <UiButton
            v-if="i !== 0"
            is-icon
            type="negative"
            size="big"
            icon="times"
            @click="popUrl(i)"
          />
        </div>
        <UiButton
          v-if="!id"
          :disabled="urls.length >= 20"
          icon="plus"
          @click="addUrl"
        >
          {{ $t('mediaBuying.blacklistUrls.add') }}
        </UiButton>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isLoading"
          @click="handleSave"
        >
          {{ id ? $t('mediaBuying.blacklistUrls.edit') : $t('mediaBuying.blacklistUrls.add') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
